import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Edit, Delete, Print as PrintIcon } from "@mui/icons-material";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { Months } from "../../../../utils";
import useDelete from "../../../../api/useDelete";
import useUpdate from "../../../../api/useUpdate";
import EditEvaluationDialog from "./EditEvaluationDialog";
import { useTranslation } from "react-i18next";
import EmployeeEvaluationPrint from "./EmployeeEvaluationPrint";

const EvaluationList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [filters, setFilters] = useState({
    month: "",
    year: "",
    employee: "",
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [currentEvaluation, setCurrentEvaluation] = useState(null);
  const [employeeData, setEmployeeData] = useState([]);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const { data: employeeFetchData } = useFetchObjects(
    ["employee"],
    "users/",
    token
  );

  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["employee-evaluations", filters],
    `employee-evaluations/?month=${filters.month}&year=${filters.year}&employee=${filters.employee}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete(
    "employee-evaluations",
    token
  );

  const { handleUpdate } = useUpdate("employee-evaluations", token);

  useEffect(() => {
    refetch();
    setEmployeeData(employeeFetchData || []);
  }, [filters, refetch, employeeFetchData]);

  const handleEditClick = (evaluation) => {
    setCurrentEvaluation(evaluation);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentEvaluation(null);
  };

  const handleSaveChanges = (editedEvaluation) => {
    handleUpdate(editedEvaluation.id, editedEvaluation);
    handleCloseDialog();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrintClick = (evaluation) => {
    setPrintData([evaluation]); // Single evaluation data
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container justifyContent="center" mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            padding: 3,
            boxShadow: 2,
          }}
        >
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: "primary.main",
              marginBottom: 4,
            }}
          >
            {t("hr.filterEvaluations")}
          </Typography>

          {/* Filters Section */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                fullWidth
                label={t("hr.month")}
                size="small"
                variant="outlined"
              >
                {Months.map((month) => (
                  <MenuItem key={month.id} value={month.id}>
                    {month.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                fullWidth
                label={t("hr.year")}
                size="small"
                variant="outlined"
              >
                {Array.from(
                  { length: 5 },
                  (_, index) => new Date().getFullYear() - index
                ).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="employee"
                value={filters.employee}
                onChange={handleFilterChange}
                fullWidth
                label={t("hr.employee")}
                size="small"
                variant="outlined"
              >
                {employeeData.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {`${emp.first_name} ${emp.last_name}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Table */}
          {isLoading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Box color="error.main" mt={4} textAlign="center">
              {t("hr.errorFetchingData")}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 2, borderRadius: 2 }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">{t("hr.month")}</TableCell>
                    <TableCell align="center">{t("hr.year")}</TableCell>
                    <TableCell align="center">{t("hr.employee")}</TableCell>
                    <TableCell align="center">{t("hr.rate1")}</TableCell>
                    <TableCell align="center">{t("hr.description")}</TableCell>
                    <TableCell align="center">{t("hr.dateCreated")}</TableCell>
                    <TableCell align="center">{t("hr.action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {data?.map((evaluation, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {evaluation.month_display}
                      </TableCell>
                      <TableCell align="center">{evaluation.year}</TableCell>
                      <TableCell align="center">
                        {evaluation.employee_display}
                      </TableCell>
                      <TableCell align="center">{evaluation.rate}</TableCell>
                      <TableCell align="center">
                        {evaluation.description}
                      </TableCell>
                      <TableCell align="center">
                        {new Date(evaluation.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            onClick={() => handleEditClick(evaluation)}
                            color="primary"
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(evaluation.id)}
                            color="error"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                          <IconButton
                            onClick={() => handlePrintClick(evaluation)}
                            color="secondary"
                            size="small"
                          >
                            <PrintIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {currentEvaluation && (
            <EditEvaluationDialog
              open={openDialog}
              onClose={handleCloseDialog}
              evaluation={currentEvaluation}
              employees={employeeData}
              onSave={handleSaveChanges}
            />
          )}

          {/* Print Content */}
          <div ref={printRef} style={{ display: "none" }}>
            <EmployeeEvaluationPrint leaves={printData} />
          </div>
        </Box>
      </Grid>
      <ConfirmDialog />
    </Grid>
  );
};

export default EvaluationList;
